/**
 * The once-per-session memory behind the forced redirect into guided Rock
 * setup: once a person has been sent to (or has opened) the setup for a
 * connection in this browser session, the app never forces them back, so
 * "Save and exit" isn't fought. Storage can be blocked (private windows):
 * then an in-memory set keeps the promise for this page load.
 */

const VISITED_KEY_PREFIX = 'graphit.rockSetup.visited.'
const memoryVisited = new Set()

/**
 * @param {string} connectionId
 * @returns {boolean}
 */
export function wasRockSetupVisited(connectionId) {
  if (memoryVisited.has(connectionId)) return true
  try {
    return window.sessionStorage.getItem(VISITED_KEY_PREFIX + connectionId) === '1'
  } catch {
    return false
  }
}

/** @param {string} connectionId */
export function markRockSetupVisited(connectionId) {
  memoryVisited.add(connectionId)
  try {
    window.sessionStorage.setItem(VISITED_KEY_PREFIX + connectionId, '1')
  } catch {
    // Storage blocked: the in-memory set above still holds for this load.
  }
}

/** Test seam: forget every visit (storage and memory). */
export function resetRockSetupVisits() {
  memoryVisited.clear()
  try {
    Object.keys(window.sessionStorage)
      .filter((key) => key.startsWith(VISITED_KEY_PREFIX))
      .forEach((key) => window.sessionStorage.removeItem(key))
  } catch {
    // Nothing to clear.
  }
}

const MANUAL_KEY_PREFIX = 'graphit.rockSetup.manualMeasurements.'

/**
 * Whether the person chose "Choose manually" on Measurements for this
 * connection, so a reload keeps the checklist (and Continue) instead of
 * falling back to the unstarted "Let Graphit set this up" card (E07 review).
 *
 * @param {string} connectionId
 * @returns {boolean}
 */
export function readManualMeasurements(connectionId) {
  try {
    return window.sessionStorage.getItem(MANUAL_KEY_PREFIX + connectionId) === '1'
  } catch {
    return false
  }
}

/**
 * @param {string} connectionId
 * @param {boolean} isManual
 */
export function writeManualMeasurements(connectionId, isManual) {
  try {
    if (isManual) window.sessionStorage.setItem(MANUAL_KEY_PREFIX + connectionId, '1')
    else window.sessionStorage.removeItem(MANUAL_KEY_PREFIX + connectionId)
  } catch {
    // Storage blocked: the choice holds for this page load only.
  }
}
